/**
 * Split post HTML after its first real top-level paragraph, so a template
 * can put a block between the two parts.
 *
 * "Top level" means the </p> closes at nesting depth 0: a paragraph inside a
 * list, table, blockquote or figure never matches. A paragraph with fewer
 * than `minChars` characters of text (an empty spacer, an image caption, a
 * one-line kicker) is skipped. When no paragraph qualifies, or the tags do
 * not balance, the function returns null and the caller shows no block.
 *
 * Pure string work, the same on the server and in the browser, so the server
 * HTML and the hydrated HTML match.
 */
const VOID_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "source",
  "track",
  "wbr",
]);

const TAG_RE = /<(\/?)([a-zA-Z][a-zA-Z0-9-]*)\b(?:[^>"']|"[^"]*"|'[^']*')*?(\/?)>/g;

function textLength(html: string): number {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;/g, " ")
    .replace(/\s+/g, " ")
    .trim().length;
}

export function splitAfterFirstParagraph(
  html: string,
  minChars = 60,
): [before: string, after: string] | null {
  if (!html) return null;
  let depth = 0;
  let pStart = -1;
  TAG_RE.lastIndex = 0;
  for (let m = TAG_RE.exec(html); m; m = TAG_RE.exec(html)) {
    const closing = m[1] === "/";
    const tag = m[2]!.toLowerCase();
    if (closing) {
      if (depth === 0) return null; // unbalanced: do not guess
      depth -= 1;
      if (tag === "p" && depth === 0 && pStart >= 0) {
        const end = m.index + m[0].length;
        if (textLength(html.slice(pStart, end)) >= minChars) {
          const after = html.slice(end);
          return textLength(after) > 0 ? [html.slice(0, end), after] : null;
        }
        pStart = -1;
      }
      continue;
    }
    if (m[3] === "/" || VOID_TAGS.has(tag)) continue;
    if (tag === "p" && depth === 0) pStart = m.index;
    depth += 1;
  }
  return null;
}
